import { Nullable } from "common/types";
import Handlebars from "handlebars";
import { addDays, format } from "date-fns";

export const createHandlebars = () => {
  Handlebars.registerHelper("ifCond", ifCond);
  Handlebars.registerHelper("formatCurrency", formatCurrency);
  Handlebars.registerHelper("formatDate", formatDate);
  Handlebars.registerHelper("ifTrue", ifTrue);
  Handlebars.registerHelper("moneyOperation", moneyOperation);
  Handlebars.registerHelper("round", round);
  Handlebars.registerHelper("addDays", addAndFormatDays);
  Handlebars.registerHelper("mathOperation", mathOperation);
  Handlebars.registerHelper("stringIncludes", stringIncludes);
  Handlebars.registerHelper("setVar", setVar);
  Handlebars.registerHelper("concat", concat);
  Handlebars.registerHelper("ifEmpty", ifEmpty);
  Handlebars.registerHelper("ifNotEmpty", ifNotEmpty);
  Handlebars.registerHelper("startsWith", startsWith);
  Handlebars.registerHelper("endsWith", endsWith);
  Handlebars.registerHelper("ifStartsWith", ifStartsWith);
  Handlebars.registerHelper("ifEndsWith", ifEndsWith);
  Handlebars.registerHelper("ifStringIncludes", ifStringIncludes);
  Handlebars.registerHelper("ifPropInObjArray", ifPropInObjArray);
  Handlebars.registerHelper("ifInArray", ifInArray);
  Handlebars.registerHelper("ifPropInEstimateGroups", ifPropInEstimateGroups);
  Handlebars.registerHelper("split", split);
  Handlebars.registerHelper("findArrayItemByProperty", findArrayItemByProperty);

  return {
    compile: (value: any, locals: any) => Handlebars.compile(value)(locals),
  };
};

export const split = function (object: any, separator: any, option: any) {
  if (
    object === undefined ||
    object === null ||
    separator === undefined ||
    separator === null ||
    option === undefined ||
    option === null
  ) {
    return "";
  }

  const value = object.split(separator)[option];

  if (value === object) {
    return "";
  }

  return value;
};

export const findArrayItemByProperty = function (
  query: unknown,
  object: any,
  query_property: any,
  output: any,
  defaultValue: any,
) {
  if (
    query === undefined ||
    query === null ||
    object === undefined ||
    object === null ||
    query_property === undefined ||
    query_property === null ||
    output === undefined ||
    output === null ||
    defaultValue === undefined ||
    defaultValue === null
  ) {
    return "";
  }

  const data = object.find((e: any) => e[query_property] === query);
  if (data) {
    return data[output];
  } else {
    return defaultValue;
  }
};

export const formatCurrency = function (value: Nullable<number>) {
  if (value === undefined || value === null) {
    return "$0.00";
  }

  // Create a new instance of Intl.NumberFormat for currency formatting
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // Use the formatter to format the value
  return formatter.format(value);
};

export const addAndFormatDays = function (value: Nullable<string>, days: number) {
  if (value === undefined || value === null) {
    return "";
  }

  // Add days to the date
  const newDate = addDays(new Date(value), days);

  // Format the date
  return format(newDate, "MM/dd/yyyy");
};

export const formatDate = function (value: any) {
  if (value === undefined || value === null) {
    return "";
  }

  return format(value, "MM/dd/yyyy");
};

export const ifTrue = function (value: any, options: any): string {
  if (value === "true" || value === true) {
    // @ts-ignore
    return options.fn(this);
  } else {
    // @ts-ignore
    return options.inverse(this);
  }
};

export const round = function (value?: any, operator?: any, precision?: any) {
  if (
    value === undefined ||
    value === null ||
    operator === undefined ||
    operator === null ||
    precision === undefined ||
    precision === null
  ) {
    return "0";
  }

  let new_value: any = 0;
  const precisionValue = Math.pow(10, precision);

  switch (operator) {
    case "ceil":
      new_value = Math.ceil(value * precisionValue) / precisionValue;
      break;
    case "floor":
      new_value = Math.floor(value * precisionValue) / precisionValue;
      break;
    case "normal":
      new_value = Math.round(value * precisionValue) / precisionValue;
      break;
    default:
  }

  return new_value.toString();
};

export const stringIncludes = function (variable: any, search_string: any) {
  if (!variable || variable === "") {
    return "Pattern Error: Missing variable parameter sent to stringIncludes";
  }

  if (!search_string || search_string === "") {
    return "Pattern Error: Missing search_string parameter sent to stringIncludes";
  }

  return variable.indexOf(search_string) !== -1;
};

export const moneyOperation = function (v1?: any, operator?: any, v2?: any) {
  if (v1 === undefined || v1 === null || v2 === undefined || v2 === null) {
    return "$0.00";
  }

  let value: any = 0;
  switch (operator) {
    case "+":
      value = v1 + v2;
      break;
    case "*":
      value = v1 * v2;
      break;
    case "-":
      value = v1 - v2;
      break;
    case "/":
      value = v1 / v2;
      break;
    default:
  }

  return formatCurrency(value);
};

export const mathOperation = function (v1?: any, operator?: any, v2?: any) {
  if (v1 === undefined || v1 === null || v2 === undefined || v2 === null) {
    return 0.0;
  }

  let value: any = 0;
  switch (operator) {
    case "+":
      value = v1 + v2;
      break;
    case "*":
      value = v1 * v2;
      break;
    case "-":
      value = v1 - v2;
      break;
    case "/":
      value = v1 / v2;
      break;
    default:
  }

  return value;
};

export const setVar = function (varName: any, varValue: any, options: any) {
  if (!varName) {
    return "Pattern Error: No variable name sent to setVar";
  }
  options.data.root[varName] = varValue;
};

export const concat = function (v1: any, v2: any) {
  let value1 = "";
  let value2 = "";
  if (v1) {
    value1 = v1;
  }
  if (v2) {
    value2 = v2;
  }
  return value1 + value2;
};

export const ifEmpty = function (varValue: any, options: any) {
  // @ts-ignore
  return !varValue || varValue === undefined || varValue.length === 0 ? options.fn(this) : options.inverse(this);
};

export const ifNotEmpty = function (varValue: any, options: any) {
  // @ts-ignore
  return !varValue || varValue === undefined || varValue.length === 0 ? options.inverse(this) : options.fn(this);
};

export const ifCond = function (v1: any, operator: any, v2: any, options: any) {
  switch (operator) {
    case "==":
      // @ts-ignore
      return v1 === v2 ? options.fn(this) : options.inverse(this);
    case "===":
      // @ts-ignore
      return v1 === v2 ? options.fn(this) : options.inverse(this);
    case "!=":
      // @ts-ignore
      return v1 !== v2 ? options.fn(this) : options.inverse(this);
    case "!==":
      // @ts-ignore
      return v1 !== v2 ? options.fn(this) : options.inverse(this);
    case "<":
      // @ts-ignore
      return v1 < v2 ? options.fn(this) : options.inverse(this);
    case "<=":
      // @ts-ignore
      return v1 <= v2 ? options.fn(this) : options.inverse(this);
    case ">":
      // @ts-ignore
      return v1 > v2 ? options.fn(this) : options.inverse(this);
    case ">=":
      // @ts-ignore
      return v1 >= v2 ? options.fn(this) : options.inverse(this);
    case "&&":
      // @ts-ignore
      return v1 && v2 ? options.fn(this) : options.inverse(this);
    case "||":
      // @ts-ignore
      return v1 || v2 ? options.fn(this) : options.inverse(this);
    default:
      // @ts-ignore
      return options.inverse(this);
  }
};

export const startsWith = function (varValue: any, searchVar: any, options: any) {
  if (!varValue || !searchVar) {
    return false;
  }

  return varValue.startsWith(searchVar);
};

export const ifStartsWith = function (varValue: any, searchVar: any, options: any) {
  if (!varValue || !searchVar) {
    // @ts-ignore
    return options.inverse(this);
  }

  // @ts-ignore
  return varValue.startsWith(searchVar) === true ? options.fn(this) : options.inverse(this);
};

export const ifEndsWith = function (varValue: any, searchVar: any, options: any) {
  if (!varValue || !searchVar) {
    // @ts-ignore
    return options.inverse(this);
  }

  // @ts-ignore
  return varValue.endsWith(searchVar) === true ? options.fn(this) : options.inverse(this);
};

export const ifStringIncludes = function (varValue: any, searchVar: any, options: any) {
  if (!varValue || !searchVar) {
    // @ts-ignore
    return options.inverse(this);
  }

  // @ts-ignore
  return varValue.indexOf(searchVar) !== -1 ? options.fn(this) : options.inverse(this);
};

export const endsWith = function (varValue: any, searchVar: any, options: any) {
  if (!varValue || !searchVar) {
    return false;
  }

  return varValue.endsWith(searchVar);
};

export const ifPropInEstimateGroups = function (
  estimateGroups: any,
  searchArray: any,
  searchProperty: any,
  searchVar: any,
  options: any,
) {
  if (!estimateGroups || !searchArray || !searchProperty || !searchVar) {
    // @ts-ignore
    return options.inverse(this);
  }

  if (Array.isArray(estimateGroups) === false) {
    // @ts-ignore
    return options.inverse(this);
  }

  // @ts-ignore
  return estimateGroups.some((estimateGroup) => {
    const v = estimateGroup[searchArray] || [];

    //@ts-ignore
    return v.some((e) => e[searchProperty] === searchVar);
  }) === true
    ? //@ts-ignore
      options.fn(this)
    : //@ts-ignore
      options.inverse(this);
};

export const ifPropInObjArray = function (searchArray: any, searchProperty: any, searchVar: any, options: any) {
  if (!searchArray || !searchProperty || !searchVar) {
    // @ts-ignore
    return options.inverse(this);
  }

  if (Array.isArray(searchArray) === false) {
    // @ts-ignore
    return options.inverse(this);
  }

  // @ts-ignore
  return searchArray.some((e) => e[searchProperty] === searchVar) === true ? options.fn(this) : options.inverse(this);
};

export const ifInArray = function (searchArray: any, searchVar: any, options: any) {
  if (!searchArray || !searchVar) {
    // @ts-ignore
    return options.inverse(this);
  }

  if (Array.isArray(searchArray) === false) {
    // @ts-ignore
    return options.inverse(this);
  }

  const blah = searchArray.includes(searchVar);

  // @ts-ignore
  return blah === true ? options.fn(this) : options.inverse(this);
};

export const handlebars = createHandlebars();
